/**
 * CookieBanner — minimal GDPR-friendly cookie banner.
 *
 * UK ICO + GDPR Art 6/7 require explicit consent for non-essential cookies
 * BEFORE they're set. We assume PostHog is the only non-essential script;
 * everything else (Stripe, auth JWT) is strictly necessary and exempt.
 *
 * Choice persists in localStorage. Opt-out fires window.posthog?.opt_out_capturing()
 * if PostHog is loaded.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "stagvault_cookie_consent_v1";

function applyConsent(choice) {
  if (typeof window === "undefined") return;
  try {
    if (choice === "rejected" && window.posthog?.opt_out_capturing) {
      window.posthog.opt_out_capturing();
    }
    if (choice === "accepted" && window.posthog?.opt_in_capturing) {
      window.posthog.opt_in_capturing();
    }
  } catch { /* PostHog not loaded — no-op */ }
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let existing = null;
    try { existing = localStorage.getItem(STORAGE_KEY); } catch { /* private mode */ }
    if (!existing) {
      // Defer 1.2s so it doesn't fight the page load / above-the-fold render
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
    applyConsent(existing);
    return undefined;
  }, []);

  function persist(choice) {
    try { localStorage.setItem(STORAGE_KEY, choice); } catch { /* no-op */ }
    applyConsent(choice);
    if (choice === "accepted") window.dispatchEvent(new Event("stagvault:consent"));
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-2 z-[70] animate-in slide-in-from-bottom duration-300 sm:inset-x-4"
      style={{ bottom: "calc(var(--sv-sticky-h, 0px) + env(safe-area-inset-bottom, 0px) + 0.5rem)" }}
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Cookie consent"
      aria-describedby="cookie-banner-description"
    >
      <div className="mx-auto flex max-w-4xl items-center gap-2 border border-zinc-700 bg-[#0a0a0a]/95 py-1 pl-3 pr-1 shadow-2xl backdrop-blur-md sm:gap-4 sm:px-5 sm:py-3">
        <div id="cookie-banner-description" className="min-w-0 flex-1 text-xs leading-snug text-zinc-300 sm:text-sm sm:leading-relaxed">
          <span className="sm:hidden">Essential cookies only, or allow analytics? </span>
          <span className="hidden sm:inline">Essential cookies keep login and payments working. Anonymous analytics are optional. </span>
          <Link to="/privacy" className="sv-no-min text-purple-300 underline underline-offset-2 transition-colors hover:text-purple-200" data-testid="cookie-banner-privacy-link">
            Privacy
          </Link>
        </div>
        <div className="flex shrink-0 gap-1 sm:gap-2">
          <button
            onClick={() => persist("rejected")}
            className="min-h-11 px-2.5 text-xs font-semibold uppercase tracking-wider text-zinc-400 transition-colors hover:text-zinc-200 sm:px-4"
            data-testid="cookie-banner-reject"
          >
            Decline
          </button>
          <button
            onClick={() => persist("accepted")}
            className="min-h-11 bg-purple-400 px-3.5 text-xs font-bold uppercase tracking-wider text-black transition-colors hover:bg-purple-300 sm:px-5"
            data-testid="cookie-banner-accept"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
